import { useEffect, useState, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { History, Loader2, MailWarning, MessageSquare, PhoneOff, RotateCw, ShieldAlert, UserX } from 'lucide-react';
import { CancelarItemModal } from '@/components/pedido/CancelarItemModal';
import { cn } from '@/lib/utils';
import {
  useAvisarLinhaCancelada, useErroBilhete, useOpcoesDaFila, usePortabilidadeHistorico, usePreviaDaLinha, useRegistrarTratativa,
  useReenviarSms, useTitularidadeSms, diasDesde, horasDesde, type ContatoTitularidade, type ExtrasPortabilidade,
} from '@/hooks/usePortabilidade';
import { diasUteisDesde } from '@/lib/diasUteis';
import { TRIAGEM_SELO_BASE, TRIAGEM_SELO_OK, TRIAGEM_SELO_PENDENTE } from '@/components/kanban/triagem/triagemTabela';

export const COLUNA_DIVERGENTE = 'CPF / CNPJ DIVERGENTE';
/** Mediana da inspeção até a PN concluída é de 4 a 5 dias: 6+ é atraso. */
const DIAS_ATRASO_PRE_ATIVO = 6;

const fmtDataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
});

/** Há quanto tempo a linha está no Pré-Ativo — o relógio que a coluna zerava. */
export function SeloPreAtivo({ extras }: { extras?: ExtrasPortabilidade }) {
  const dias = diasDesde(extras?.entrou_pre_ativo_em);
  if (dias === null) return <span className="text-[10px] text-muted-label">—</span>;
  const atrasado = dias >= DIAS_ATRASO_PRE_ATIVO;
  return (
    <span
      className={cn(TRIAGEM_SELO_BASE, atrasado ? TRIAGEM_SELO_PENDENTE : 'text-muted-foreground')}
      title={`No Pré-Ativo desde ${fmtDataHora(extras!.entrou_pre_ativo_em!)} — o tempo na coluna zera a cada troca de fila; este não.`}
    >
      {dias}d no pré-ativo
    </span>
  );
}

/** A régua automática do SMS: o que já saiu para o cliente. */
export function SeloReguaSms({ extras }: { extras?: ExtrasPortabilidade }) {
  if (!extras) return <span className="text-[10px] text-muted-label">—</span>;
  const passos = [
    { rotulo: 'Aviso', em: extras.sms_msg1_em },
    { rotulo: 'Imagem', em: extras.sms_msg2_em },
    { rotulo: 'Cobrança 24h', em: extras.sms_cobranca_em },
  ];
  const titulo = [
    ...passos.map(p => `${p.rotulo}: ${p.em ? fmtDataHora(p.em) : 'não saiu'}`),
    extras.reenvios ? `Reenvios do SMS: ${extras.reenvios} (último ${fmtDataHora(extras.ultimo_reenvio_em!)})` : 'Nenhum reenvio do SMS',
  ].join('\n');
  return (
    <span className="flex items-center gap-1" title={titulo}>
      {passos.map(p => (
        <span key={p.rotulo} className={cn('h-1.5 w-1.5 rounded-full', p.em ? 'bg-primary' : 'bg-border')} />
      ))}
      {extras.reenvios > 0 && (
        <span className={cn(TRIAGEM_SELO_BASE, TRIAGEM_SELO_PENDENTE)}>{extras.reenvios}× reenvio</span>
      )}
    </span>
  );
}

/** Depois do SIM a data sai em até 3 dias úteis: com 3 ou mais, a data já devia estar aqui. */
export const DIAS_UTEIS_DATA_ATRASADA = 3;
const HACHURA = 'bg-[repeating-linear-gradient(135deg,#5B0A0D_0_3px,#B4808C_3px_6px)] dark:bg-[repeating-linear-gradient(135deg,#C24545_0_3px,#5E2229_3px_6px)]';

/**
 * SEM DATA (Douglas 18/10/2026). De 27/09 a 17/10, 25 cotações tiveram a data
 * lançada depois da troca já feita (média de 13 h), junto com o concluído: o
 * cliente ficou sem o aviso da data. O selo cobra a data enquanto ela ainda serve.
 * Não trava, não abre janela e não muda a fila; some quando a BKO agenda.
 */
export function SeloSemData({ extras }: { extras?: ExtrasPortabilidade }) {
  const dias = diasUteisDesde(extras?.entrou_coluna_em);
  if (!dias) return null;
  const atrasada = dias >= DIAS_UTEIS_DATA_ATRASADA;
  return (
    <span
      className={cn(TRIAGEM_SELO_BASE, TRIAGEM_SELO_PENDENTE, 'whitespace-nowrap')}
      title={`Em SMS ENVIADO desde ${fmtDataHora(extras!.entrou_coluna_em!)}. Depois do SIM a data sai em até 3 dias úteis: consulte a data e agende.`}
    >
      {atrasada && <span className={cn('h-2 w-2 flex-shrink-0 rounded-[2px]', HACHURA)} />}
      sem data · {dias} {dias === 1 ? 'dia útil' : 'dias úteis'}
    </span>
  );
}

/** O cliente escreveu e ninguém respondeu (Douglas 22/10/2026): aviso, não trava. */
export function SeloClienteSemResposta({ extras }: { extras?: ExtrasPortabilidade }) {
  const desde = extras?.cliente_esperando_desde;
  if (!desde) return null;
  const minutos = Math.max(0, Math.round((Date.now() - new Date(desde).getTime()) / 60_000));
  const texto = minutos < 60 ? `${minutos} min` : minutos < 1440 ? `${Math.floor(minutos / 60)} h` : `${Math.floor(minutos / 1440)} d`;
  return (
    <span
      className={cn(TRIAGEM_SELO_BASE, TRIAGEM_SELO_PENDENTE, 'whitespace-nowrap')}
      title={`O cliente escreveu em ${fmtDataHora(desde)} e a conversa está sem resposta. Abra a conversa do pedido no Inbox.`}
    >
      cliente sem resposta · {texto}
    </span>
  );
}

/** O que a operadora disse depois da hora marcada: evidência, não prova. */
export function SeloOperadora({ extras }: { extras?: ExtrasPortabilidade }) {
  const op = extras?.operadora;
  if (!op) return <span className="text-[10px] text-muted-label">aguardando consulta</span>;
  return (
    <span
      className={cn(TRIAGEM_SELO_BASE, op.claro ? TRIAGEM_SELO_OK : TRIAGEM_SELO_PENDENTE, 'max-w-full truncate')}
      title={`Consulta de ${fmtDataHora(op.em)}. É evidência, não prova: a API pode demorar a refletir a portabilidade. Quem conclui é a BKO.`}
    >
      {op.resumo}
    </span>
  );
}

/* ── A CONSULTA NA CLARO, LINHA POR LINHA (Douglas 28/10/2026, maquete v4) ──
 * "a lógica é consultar todos rapidamente e ter a tratativa pelos botões".
 * Cada diálogo mostra o que vai sair antes de apertar, e cada botão registra a
 * tratativa na linha e a manda para o fim da fila. Ela volta em 24 horas. */

export interface AlvoLinha {
  itemId: string;
  pedidoId: string;
  linha: string;
  razaoSocial: string;
  numeroPedido: string;
}

const linkConversa = (id: string) => `/whatsapp-inbox?conversation=${id}`;

function Previa({ titulo, carregando, children }: { titulo: string; carregando: boolean; children: ReactNode }) {
  return (
    <div className="grid gap-1">
      <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-label">{titulo}</Label>
      <div className="min-h-[64px] whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 text-xs text-foreground">
        {carregando ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : children}
      </div>
    </div>
  );
}

function AbrirConversa({ conversaId }: { conversaId?: string | null }) {
  if (!conversaId) return null;
  return (
    <a href={linkConversa(conversaId)} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
      <MessageSquare className="h-3.5 w-3.5" />Abrir a conversa do pedido no Inbox
    </a>
  );
}

/** O último resultado da consulta na linha: o que a BKO já fez e o que espera. */
export function SeloResultado({ extras }: { extras?: ExtrasPortabilidade }) {
  const r = extras?.resultado;
  if (!r) return null;
  const horas = horasDesde(r.em) ?? 0;
  const quando = horas < 1 ? 'agora' : horas < 24 ? `há ${Math.floor(horas)} h` : `há ${Math.floor(horas / 24)} d`;
  const volta = horas >= 24;
  const base = cn(TRIAGEM_SELO_BASE, 'whitespace-nowrap');
  const titulo = `${fmtDataHora(r.em)}${r.por ? ` · ${r.por}` : ''}${volta ? ' · passou de 24 h: consultar de novo' : ''}`;
  if (r.tipo === 'titularidade') {
    return (
      <span className={cn(base, TRIAGEM_SELO_PENDENTE)} title={`Conflito de titularidade desde ${fmtDataHora(r.desde || r.em)}. Último contato: ${titulo}`}>
        {volta && <span className={cn('h-2 w-2 flex-shrink-0 rounded-[2px]', HACHURA)} />}
        titularidade · {volta ? 'falar com o cliente' : quando}
      </span>
    );
  }
  if (r.tipo === 'erro_bilhete') {
    if (r.resposta_em) {
      return (
        <span className={cn(base, TRIAGEM_SELO_OK)} title={`A Claro respondeu em ${fmtDataHora(r.resposta_em)}. Leia na atendimento@ e lance a data.`}>
          Claro respondeu
        </span>
      );
    }
    return (
      <span className={cn(base, TRIAGEM_SELO_PENDENTE)} title={`${r.tentativa ?? 1}º e-mail do erro no bilhete: ${titulo}`}>
        {volta && <span className={cn('h-2 w-2 flex-shrink-0 rounded-[2px]', HACHURA)} />}
        aguardando Claro · {quando}
      </span>
    );
  }
  return (
    <span className={cn(base, volta ? TRIAGEM_SELO_PENDENTE : 'text-muted-foreground')} title={`SMS reenviado pelo forms da Claro: ${titulo}`}>
      SMS reenviado · {quando}
    </span>
  );
}

const MOTIVO_SMS_SEM_RESPOSTA = 'Consultei na Claro: SMS sem resposta. Reenviei a confirmação pelo forms da Claro.';

export function ReenviarSmsDialog({ alvo, reenvios, onClose }: {
  alvo: AlvoLinha | null; reenvios: number; onClose: () => void;
}) {
  const [motivo, setMotivo] = useState(MOTIVO_SMS_SEM_RESPOSTA);
  const reenviar = useReenviarSms();
  const previa = usePreviaDaLinha(alvo?.itemId ?? null, 'sms');
  useEffect(() => { if (alvo) setMotivo(MOTIVO_SMS_SEM_RESPOSTA); }, [alvo]);
  return (
    <Dialog open={!!alvo} onOpenChange={o => { if (!o && !reenviar.isPending) onClose(); }}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RotateCw className="h-5 w-5 text-primary" />SMS sem resposta · linha {alvo?.linha}</DialogTitle>
          <DialogDescription>
            {alvo?.razaoSocial} · tentativa {reenvios + 1}. Reenvie a confirmação pelo forms da Claro e registre aqui: o cliente
            recebe a mensagem abaixo, a linha vai para o fim da fila e volta em 24 horas.
          </DialogDescription>
        </DialogHeader>
        <Previa titulo="Mensagem ao cliente" carregando={previa.isLoading}>{previa.data?.texto ?? (previa.error as Error | null)?.message}</Previa>
        <div className="grid gap-1">
          <Label className="text-xs font-semibold">O que você viu na Claro</Label>
          <Textarea rows={2} value={motivo} onChange={e => setMotivo(e.target.value)} disabled={reenviar.isPending} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={reenviar.isPending}>Cancelar</Button>
          <Button disabled={motivo.trim().length < 5 || reenviar.isPending || !previa.data}
            onClick={() => alvo && reenviar.mutate({ itemId: alvo.itemId, motivo: motivo.trim() }, { onSuccess: onClose })}>
            {reenviar.isPending ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Enviando…</> : 'Registrar e avisar o cliente'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CONTATOS: { valor: ContatoTitularidade; rotulo: string }[] = [
  { valor: 'liguei', rotulo: 'Liguei' },
  { valor: 'mensagem', rotulo: 'Mandei mensagem' },
  { valor: 'respondeu', rotulo: 'O cliente respondeu' },
];

/**
 * CONFLITO DE TITULARIDADE fica na fila SMS (Douglas 28/10/2026): "não pode
 * passar para outra fila ainda" · "tem que se comunicar com o cliente
 * obrigatoriamente". A 1ª vez manda ao cliente o titular que está no pedido;
 * nas voltas de 24 h a BKO diz como falou com ele. Corrigido o titular no
 * pedido, ela reenvia pelo forms e aperta SMS sem resposta.
 */
export function TitularidadeSmsDialog({ alvo, onClose }: { alvo: AlvoLinha | null; onClose: () => void }) {
  const previa = usePreviaDaLinha(alvo?.itemId ?? null, 'titularidade');
  const registrar = useTitularidadeSms();
  const [contato, setContato] = useState<ContatoTitularidade | null>(null);
  const [nota, setNota] = useState('');
  const [enviadoConversa, setEnviadoConversa] = useState<string | null | undefined>(undefined);
  useEffect(() => { if (alvo) { setContato(null); setNota(''); setEnviadoConversa(undefined); } }, [alvo]);
  const aberta = !!previa.data?.aberta;
  const pode = !registrar.isPending && !!previa.data && (!aberta || !!contato);
  return (
    <Dialog open={!!alvo} onOpenChange={o => { if (!o && !registrar.isPending) onClose(); }}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserX className="h-5 w-5 text-primary" />Conflito de titularidade · linha {alvo?.linha}</DialogTitle>
          <DialogDescription>
            {alvo?.razaoSocial} · pedido {alvo?.numeroPedido}. A linha fica nesta fila com o selo titularidade e volta a cada
            24 horas até o cliente mandar o titular certo.
          </DialogDescription>
        </DialogHeader>
        {enviadoConversa !== undefined ? (
          <div className="grid gap-3">
            <p className="text-sm text-foreground">Mensagem enviada ao cliente pela conversa do pedido. Continue a conversa no Inbox.</p>
            <AbrirConversa conversaId={enviadoConversa} />
            <div className="flex justify-end"><Button onClick={onClose}>Fechar</Button></div>
          </div>
        ) : (
          <>
            {aberta ? (
              <div className="grid gap-2">
                <p className="text-xs text-muted-foreground">
                  A mensagem já foi mandada{previa.data?.desde ? ` em ${fmtDataHora(previa.data.desde)}` : ''}. Diga como falou com o cliente hoje (obrigatório).
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {CONTATOS.map(c => (
                    <button key={c.valor} type="button" onClick={() => setContato(c.valor)}
                      className={cn('h-8 rounded-md border px-3 text-xs font-semibold transition-colors',
                        contato === c.valor ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-primary')}>
                      {c.rotulo}
                    </button>
                  ))}
                </div>
                <Textarea rows={2} value={nota} onChange={e => setNota(e.target.value)} disabled={registrar.isPending}
                  placeholder="O que ele disse (opcional). Ex.: vai mandar a fatura da Vivo amanhã." />
                <AbrirConversa conversaId={previa.data?.conversa_id} />
              </div>
            ) : (
              <Previa titulo="Mensagem ao cliente" carregando={previa.isLoading}>{previa.data?.texto ?? (previa.error as Error | null)?.message}</Previa>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={registrar.isPending}>Cancelar</Button>
              <Button disabled={!pode}
                onClick={() => alvo && registrar.mutate({ itemId: alvo.itemId, contato: aberta ? contato : null, nota: nota.trim() }, {
                  onSuccess: (r) => { if (r.primeira) setEnviadoConversa(r.conversa_id); else onClose(); },
                })}>
                {registrar.isPending ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Registrando…</> : aberta ? 'Registrar o contato' : 'Mandar ao cliente e manter na fila'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** ERRO NO BILHETE: só e-mail, pela atendimento@, aos gerentes da Claro (Douglas 28/10/2026). */
export function ErroBilheteDialog({ alvo, onClose }: { alvo: AlvoLinha | null; onClose: () => void }) {
  const previa = usePreviaDaLinha(alvo?.itemId ?? null, 'bilhete');
  const enviar = useErroBilhete();
  return (
    <Dialog open={!!alvo} onOpenChange={o => { if (!o && !enviar.isPending) onClose(); }}>
      <DialogContent className="sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MailWarning className="h-5 w-5 text-primary" />Erro no bilhete · linha {alvo?.linha}</DialogTitle>
          <DialogDescription>
            O e-mail sai pela atendimento@ e a resposta da Claro aparece na linha. Ela fica aguardando a Claro e volta em 24 horas.
          </DialogDescription>
        </DialogHeader>
        {previa.data?.para && (
          <p className="truncate text-xs text-muted-foreground" title={previa.data.para.join(', ')}>
            <span className="font-semibold text-foreground">Para:</span> {previa.data.para.join(', ')}
          </p>
        )}
        {previa.data?.assunto && (
          <p className="truncate text-xs text-muted-foreground" title={previa.data.assunto}>
            <span className="font-semibold text-foreground">Assunto:</span> {previa.data.assunto}
          </p>
        )}
        <Previa titulo="E-mail" carregando={previa.isLoading}>{previa.data?.texto ?? (previa.error as Error | null)?.message}</Previa>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={enviar.isPending}>Cancelar</Button>
          <Button disabled={enviar.isPending || !previa.data} onClick={() => alvo && enviar.mutate(alvo.itemId, { onSuccess: onClose })}>
            {enviar.isPending ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Enviando…</> : 'Mandar o e-mail à Claro'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** ID do status IRREGULARIDADE NA LINHA: os motivos de linha cancelada ou desativada moram nele. */
export const STATUS_IRREGULARIDADE_NA_LINHA = '2de6c2e3-c367-4633-8fa6-ad9118a1d631';

/**
 * LINHA CANCELADA OU DESATIVADA (Douglas 28/10/2026): cancela só esta linha com
 * o motivo real e pergunta ao cliente, pelo WhatsApp, se quer uma linha nova.
 */
export function LinhaCanceladaDialog({ alvo, onClose }: { alvo: AlvoLinha | null; onClose: () => void }) {
  const previa = usePreviaDaLinha(alvo?.itemId ?? null, 'cancelada');
  const avisar = useAvisarLinhaCancelada();
  const [cancelando, setCancelando] = useState(false);
  useEffect(() => { if (alvo) setCancelando(false); }, [alvo]);
  return (
    <>
      <Dialog open={!!alvo && !cancelando} onOpenChange={o => { if (!o && !avisar.isPending) onClose(); }}>
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PhoneOff className="h-5 w-5 text-primary" />Linha cancelada ou desativada · {alvo?.linha}</DialogTitle>
            <DialogDescription>
              Cancela só esta linha, com o motivo real: "Linha cancelada na operadora" ou "Linha desativada ou suspensa na
              operadora". Depois o cliente recebe a mensagem abaixo.
            </DialogDescription>
          </DialogHeader>
          <Previa titulo="Mensagem ao cliente" carregando={previa.isLoading}>{previa.data?.texto ?? (previa.error as Error | null)?.message}</Previa>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Voltar</Button>
            <Button onClick={() => setCancelando(true)} disabled={!previa.data}>Escolher o motivo e cancelar a linha</Button>
          </div>
        </DialogContent>
      </Dialog>
      {alvo && cancelando && (
        <CancelarItemModal
          open
          onOpenChange={(o) => { if (!o) onClose(); }}
          itemId={alvo.itemId}
          pedidoId={alvo.pedidoId}
          itemNome={`Linha ${alvo.linha}`}
          statusPreSelecionado={STATUS_IRREGULARIDADE_NA_LINHA}
          onSuccess={() => { avisar.mutate(alvo.itemId); onClose(); }}
        />
      )}
    </>
  );
}

/** Titularidade: por que está errado + o que está sendo feito. */
export function TratativaDialog({ alvo, onClose, onSalvo, rotuloSalvar = 'Salvar tratativa' }: {
  alvo: { itemId: string; pedidoId: string; razaoSocial: string; numeroPedido: string } | null;
  onClose: () => void;
  /** Depois de gravar — ex.: mandar para o fim da fila. */
  onSalvo?: () => void;
  rotuloSalvar?: string;
}) {
  const { data: opcoes = [] } = useOpcoesDaFila(COLUNA_DIVERGENTE, !!alvo);
  const registrar = useRegistrarTratativa();
  const [motivo, setMotivo] = useState('');
  const [acao, setAcao] = useState('');
  const [texto, setTexto] = useState('');
  useEffect(() => { if (alvo) { setMotivo(''); setAcao(''); setTexto(''); } }, [alvo]);
  const pode = !!motivo && !!acao && texto.trim().length >= 10 && !registrar.isPending;
  return (
    <Dialog open={!!alvo} onOpenChange={o => { if (!o && !registrar.isPending) onClose(); }}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-primary" />Tratativa de titularidade</DialogTitle>
          <DialogDescription>
            {alvo?.razaoSocial} · pedido {alvo?.numeroPedido}. Fica na timeline do pedido. Prazo desta fila: 48 horas.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1">
            <Label className="text-xs font-semibold">Por que está errado</Label>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Escolha o motivo" /></SelectTrigger>
              <SelectContent>
                {opcoes.filter(o => o.tipo === 'motivo').map(o => <SelectItem key={o.codigo} value={o.codigo} className="text-xs">{o.rotulo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs font-semibold">O que está sendo feito</Label>
            <Select value={acao} onValueChange={setAcao}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Escolha a ação" /></SelectTrigger>
              <SelectContent>
                {opcoes.filter(o => o.tipo === 'acao').map(o => <SelectItem key={o.codigo} value={o.codigo} className="text-xs">{o.rotulo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs font-semibold">Detalhe (obrigatório)</Label>
            <Textarea rows={3} value={texto} onChange={e => setTexto(e.target.value)} disabled={registrar.isPending}
              placeholder="Ex.: linha 51 99999-0000 está no CPF do sócio João; pedi a fatura da Vivo dele." />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={registrar.isPending}>Cancelar</Button>
          <Button disabled={!pode} onClick={() => alvo && registrar.mutate(
            { itemId: alvo.itemId, pedidoId: alvo.pedidoId, motivo, acao, texto },
            { onSuccess: () => { onSalvo?.(); onClose(); } })}>
            {registrar.isPending ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Salvando…</> : rotuloSalvar}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ROTULO_TIPO: Record<string, string> = {
  tratativa: 'Tratativa',
  evento_sms_reenvio: 'SMS',
  evento_aviso_vespera: 'Aviso',
  evento_consulta_operadora: 'Operadora',
  evento_titularidade: 'Titular',
  evento_erro_bilhete: 'Bilhete',
  evento_linha_cancelada: 'Cancelada',
  mudou_fila: 'Fila',
  reagendou: 'Reagendou',
};

/** Tudo o que a linha viveu no Pré-Ativo, do mais novo ao mais antigo. */
export function HistoricoPortabilidadeDialog({ alvo, onClose }: {
  alvo: { itemId: string; razaoSocial: string; numeroPedido: string } | null; onClose: () => void;
}) {
  const { data = [], isLoading } = usePortabilidadeHistorico(alvo?.itemId ?? null);
  return (
    <Dialog open={!!alvo} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-primary" />Histórico da portabilidade</DialogTitle>
          <DialogDescription>{alvo?.razaoSocial} · pedido {alvo?.numeroPedido}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : data.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">Nada registrado ainda.</p>
          ) : (
            <ul className="divide-y divide-border/50">
              {data.map((e, i) => (
                <li key={i} className="grid grid-cols-[92px_74px_1fr] gap-2 py-2 text-xs">
                  <span className="tabular-nums text-muted-foreground">{fmtDataHora(e.em)}</span>
                  <span className={cn(TRIAGEM_SELO_BASE, e.tipo === 'tratativa' ? TRIAGEM_SELO_PENDENTE : 'text-muted-foreground', 'justify-center')}>
                    {ROTULO_TIPO[e.tipo] ?? e.tipo}
                  </span>
                  <div className="min-w-0">
                    {(e.motivo || e.acao) && (
                      <p className="font-semibold text-foreground">{[e.motivo, e.acao].filter(Boolean).join(' · ')}</p>
                    )}
                    <p className="whitespace-pre-wrap break-words text-muted-foreground">{e.texto}</p>
                    {e.por && <p className="text-[10px] text-muted-label">{e.por}{e.coluna && e.tipo === 'tratativa' ? ` · ${e.coluna}` : ''}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
